import { useTranslation } from 'react-i18next'
import { Hash } from '@digital-twins/design-system/icons'
import {
  SOCIAL_PLATFORMS,
  LibraryCollectionItem,
  LibraryCollectionTopicItem,
  SocialPlatform,
  SocialUniverseItem,
} from '@digital-twins/shared-types'

import { CollectionCardLayout, CollectionItemCard, MetaDot, MetaValue } from '@/components/library/CollectionItemCard'
import { LibraryCoverImage } from '@/components/library/LibraryItemCover'
import { Badge } from '@/components/ui/badge'
import { CountryFlag } from '@/components/ui/CountryFlag'
import { cn } from '@/lib/utils'
import { ROUTES } from '@/routes/routes'
import { marketDisplayName, marketFlagCode } from '@/pages/KnowledgeHubPage/libraryHelpers'
import { SocialPlatformIcon } from '@/pages/KnowledgeHubPage/social/SocialPlatformIcon'
import { socialSentimentShares } from '@/pages/KnowledgeHubPage/social/SocialUniverseCard'
import { formatTopicStat } from '@/pages/KnowledgeHubPage/social/SocialStatRow'
import { SOCIAL_PLATFORM_LABELS, formatSocialDate } from '@/pages/KnowledgeHubPage/social/socialFormat'
import { universeChannelsNeedingRetry } from '@/pages/KnowledgeHubPage/social/socialTrackerStatus'
import { useSocialItemImage } from '@/pages/KnowledgeHubPage/social/useSocialItemImage'

export interface CollectionTopicCardProps {
  topic: LibraryCollectionTopicItem
  /** Test ids are `collection-topic-card-{index}`. */
  index: number
  /** The org's Topic list entry, when it holds this Topic: post images, market and channel health. */
  universe?: SocialUniverseItem
  /** The card's place in its section's grid. */
  layout?: CollectionCardLayout
  /** Handed to the Topic page, whose back link then names where it returns ("Back to RTD"). */
  backLabel?: string
  /** Handed to the Topic page with the label: its back link's href (the collection's own route). */
  backHref?: string
}

/** `platforms` is `Array<string>` on the wire; unknown values drop. */
export function isSocialPlatform(value: string): value is SocialPlatform {
  return (SOCIAL_PLATFORMS as ReadonlyArray<string>).includes(value)
}

const MAX_BRANDS = 3

/** Where the second and third platform marks sit on the generated cover. */
const SECONDARY_MARK_CLASSES: Array<string> = [
  'left-[9%] top-[13%] h-[26%] rotate-[8deg] opacity-[0.22]',
  'left-[36%] top-[44%] h-[15%] -rotate-6 opacity-[0.16]',
]

export interface TopicCoverProps {
  platforms: Array<SocialPlatform>
  testId: string
  isFeatured: boolean
}

/**
 * A Topic's own cover when it has no post images: a primary to accent
 * gradient with a soft highlight and a fading dot grid, its lead platform's
 * mark large in the corner and the others small, so it holds a featured
 * slot like a photo would. A Topic without a known platform shows the
 * Topic mark.
 */
export function TopicCover(props: TopicCoverProps) {
  const { platforms, testId, isFeatured } = props

  const [lead, ...rest] = platforms
  const leadClass =
    'absolute -bottom-[16%] -right-[5%] h-[84%] w-auto aspect-square rotate-[-12deg] text-primary-foreground opacity-[0.26] transition-transform duration-500 group-hover:rotate-[-8deg] group-hover:scale-105'

  return (
    <span
      className="absolute inset-0 overflow-hidden bg-gradient-to-br from-primary via-primary/85 to-chart-accent"
      aria-hidden="true"
      data-testid={testId}
      data-tone={isFeatured ? 'bold' : 'soft'}
      data-cover="topic"
    >
      <span className="absolute inset-0 bg-[radial-gradient(70%_60%_at_15%_10%,hsl(var(--primary-foreground)/0.26),transparent_70%)]" />
      <span className="absolute inset-0 bg-[radial-gradient(hsl(var(--primary-foreground)/0.2)_1px,transparent_1.5px)] bg-[length:16px_16px] [mask-image:linear-gradient(135deg,black,transparent_65%)]" />
      {lead ? (
        <SocialPlatformIcon platform={lead} className={leadClass} />
      ) : (
        <Hash className={leadClass} strokeWidth={1.5} />
      )}
      {rest.slice(0, SECONDARY_MARK_CLASSES.length).map((platform, position) => (
        <SocialPlatformIcon
          key={platform}
          platform={platform}
          className={cn('absolute aspect-square w-auto text-primary-foreground', SECONDARY_MARK_CLASSES[position])}
        />
      ))}
    </span>
  )
}

/**
 * A Topic in the collection's card language: a collage of its latest post
 * images (its own platform mark cover without any). The cover stays clear.
 * The text side carries the topic label, channel marks, market, the post
 * figures when the Topic list has them, brand chips, and the meta row
 * (posts, last sync, the other collections it is in, and an amber chip when
 * a channel could not update). One link to the Topic page. It only renders
 * inside a collection, so "in 1 collection" (this one) is left out.
 */
export function CollectionTopicCard(props: CollectionTopicCardProps) {
  const { topic, index, universe, layout = 'standard', backLabel, backHref } = props

  const { t, i18n } = useTranslation('app')
  const previewPaths = universe?.previewImagePaths ?? []
  const firstImage = useSocialItemImage(previewPaths[0] ?? null)
  const secondImage = useSocialItemImage(previewPaths[1] ?? null)
  const thirdImage = useSocialItemImage(previewPaths[2] ?? null)

  const testId = `collection-topic-card-${index}`
  const platforms = topic.platforms.filter(isSocialPlatform)
  const brands = topic.brands.slice(0, MAX_BRANDS)
  const hiddenBrands = topic.brands.length - brands.length
  const lastSynced = formatSocialDate(topic.lastSyncedAt, i18n.language)
  const troubleChannels = universe ? universeChannelsNeedingRetry(universe.channels) : []
  const coverImages: Array<LibraryCoverImage> = [firstImage.src, secondImage.src, thirdImage.src]
    .filter((src): src is string => Boolean(src))
    .map((src) => ({ src }))
  const market = universe?.market?.trim() ?? ''

  const item: LibraryCollectionItem = {
    kind: 'topic',
    token: topic.token,
    title: topic.name,
    subtitle: null,
    occurredAt: topic.lastSyncedAt ?? '',
    imageUrl: coverImages[0]?.src ?? null,
    membership: topic.membership,
    facets: [],
  }

  const shares = universe ? socialSentimentShares(universe.sentiment) : null
  const showStats = universe != null && universe.totalPosts > 0
  const notAvailable = t('socialListening.topicPage.stats.notAvailable')

  const facts = (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-muted-foreground" data-testid={`${testId}-facts`}>
      <span className="font-medium text-foreground/80" data-testid={`${testId}-kind`}>
        {t('library.collections.resources.kindSingular.topic')}
      </span>
      {platforms.length > 0 ? (
        <span className="inline-flex items-center gap-1.5" data-testid={`${testId}-platforms`}>
          {platforms.map((platform) => (
            <span key={platform} title={SOCIAL_PLATFORM_LABELS[platform]} data-testid={`${testId}-platform-${platform}`}>
              <SocialPlatformIcon platform={platform} className="h-3.5 w-3.5" />
            </span>
          ))}
        </span>
      ) : null}
      {market ? (
        <span className="inline-flex min-w-0 items-center gap-1.5" data-testid={`${testId}-market`}>
          <CountryFlag code={marketFlagCode(market)} data-testid={`${testId}-market-flag`} />
          <span className="truncate">{marketDisplayName(market, i18n.language)}</span>
        </span>
      ) : null}
    </div>
  )

  const stats = showStats && universe ? (
    <div className="flex flex-col gap-1.5" data-testid={`${testId}-stats`}>
      <dl className="grid grid-cols-3 gap-2">
        <div data-testid={`${testId}-stat-posts`}>
          <dt className="text-[12px] text-muted-foreground">{t('socialListening.topicCard.stats.posts')}</dt>
          <dd className="text-[16px] font-semibold tabular-nums text-foreground">
            {formatTopicStat(universe.totalPosts, i18n.language)}
          </dd>
        </div>
        <div data-testid={`${testId}-stat-reach`}>
          <dt className="text-[12px] text-muted-foreground">{t('socialListening.topicCard.stats.reach')}</dt>
          <dd className="text-[16px] font-semibold tabular-nums text-foreground">
            {universe.reach != null ? formatTopicStat(universe.reach, i18n.language) : notAvailable}
          </dd>
        </div>
        <div data-testid={`${testId}-stat-creators`}>
          <dt className="text-[12px] text-muted-foreground">{t('socialListening.topicCard.stats.creators')}</dt>
          <dd className="text-[16px] font-semibold tabular-nums text-foreground">
            {universe.creators != null ? formatTopicStat(universe.creators, i18n.language) : notAvailable}
          </dd>
        </div>
      </dl>
      {shares ? (
        <span className="text-[12px] text-muted-foreground tabular-nums" data-testid={`${testId}-sentiment`}>
          {t('socialListening.sentimentShare.positive', { percent: shares.positive })}
        </span>
      ) : null}
    </div>
  ) : null

  const details = (
    <div className="flex flex-col gap-2">
      {facts}
      {stats}
      {brands.length > 0 || topic.membership === 'rule' ? (
      <div className="flex flex-wrap gap-1.5">
        {topic.membership === 'rule' ? (
          <span
            className="rounded-full bg-primary/10 px-2 py-0.5 text-xs text-foreground/75"
            data-testid={`${testId}-linked`}
          >
            {t('library.collections.topics.linkedByBrand')}
          </span>
        ) : null}
        {brands.length > 0 ? (
          <span className="contents" data-testid={`${testId}-brands`}>
            {brands.map((brand) => (
              <span
                key={brand.id}
                className="max-w-full truncate rounded-full border border-card-border px-2 py-0.5 text-xs text-muted-foreground"
              >
                {brand.name}
              </span>
            ))}
            {hiddenBrands > 0 ? (
              <span className="rounded-full border border-card-border px-2 py-0.5 text-xs text-muted-foreground">
                +{hiddenBrands}
              </span>
            ) : null}
          </span>
        ) : null}
      </div>
      ) : null}
    </div>
  )

  // A zero never leads the card: a Topic without posts says so after its sync date.
  // The stat row already states the post count when the Topic list has one.
  const hasPosts = topic.posts > 0

  const metaParts = [
    hasPosts && !showStats ? (
      <MetaValue key="posts" testId={`${testId}-posts`}>
        {t('library.collections.topics.postsCount', {
          count: topic.posts,
          value: formatTopicStat(topic.posts, i18n.language),
        })}
      </MetaValue>
    ) : null,
    lastSynced ? (
      <MetaValue key="synced" testId={`${testId}-synced`}>
        {t('library.collections.topics.lastSynced', { date: lastSynced })}
      </MetaValue>
    ) : null,
    topic.collectionCount > 1 ? (
      <MetaValue key="collections" testId={`${testId}-collections`}>
        {t('library.collections.topics.inCollections', { count: topic.collectionCount })}
      </MetaValue>
    ) : null,
    hasPosts ? null : (
      <MetaValue key="no-posts" testId={`${testId}-no-posts`}>
        <span className="text-muted-foreground/80">{t('library.collections.topics.noPosts')}</span>
      </MetaValue>
    ),
  ].filter((part) => part != null)

  const meta = (
    <>
      {metaParts.flatMap((part, position) => (position === 0 ? [part] : [<MetaDot key={`dot-${position}`} />, part]))}
      {troubleChannels.length > 0 ? (
        <Badge
          variant="warning"
          className="ml-1 rounded-full px-2 py-0 text-[12px]"
          data-testid={`${testId}-trouble`}
        >
          {t('socialListening.topicCard.channelTrouble', { count: troubleChannels.length })}
        </Badge>
      ) : null}
    </>
  )

  return (
    <CollectionItemCard
      item={item}
      href={ROUTES.knowledgeTopic(topic.token)}
      linkState={backLabel ? { backLabel, backHref } : undefined}
      testId={testId}
      coverImages={coverImages}
      details={details}
      meta={meta}
      layout={layout}
      cta={t('library.collections.topics.cta')}
      coverFallback={
        <TopicCover platforms={platforms} testId={`${testId}-tint`} isFeatured={layout === 'hero' || layout === 'lead'} />
      }
    />
  )
}
